import React, { FC, memo } from 'react';
import { useSelector } from 'react-redux';

import { Campaign } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';

import { GridPageLayout } from '@lux/grid';
import { ErrorBoundary } from '@browsing/components';
import {
    OneColumnLayout,
    TwoColumnLayout,
    ThreeColumnLayout,
    FourColumnLayout,
    SquareLayout,
} from './layout';
import { GridLayoutProvider } from './context/GridLayoutContext';
import { useGridLayoutContextValue } from './context/useGridLayoutContextValue';
import { useResponsiveColumns } from './hooks';
import { CAMPAIGN_WRAPPER } from '../../constants';
import { Wrapper } from '../../mylounge-wrapper.styles';

interface Props {
    campaigns: Campaign[];
    isPlusEnabled: boolean;
}

export const Grid: FC<Props> = ({ campaigns, isPlusEnabled }) => {
    const categoryTabId = useSelector(getCategoryTabId);

    const columnCount = useResponsiveColumns();

    const gridLayoutContextValue = useGridLayoutContextValue({
        campaigns,
        isPlusEnabled,
        columnCount,
    });

    if (!campaigns) {
        return null;
    }

    const layoutMap = {
        1: <OneColumnLayout />,
        2: <TwoColumnLayout />,
        3: <ThreeColumnLayout />,
        4: <FourColumnLayout />,
    };

    // Simple grid for non-ALL categories
    if (categoryTabId !== CategoryTabIds.ALL) {
        return (
            <ErrorBoundary componentName="MyLoungeCampaigns">
                <GridPageLayout>
                    <Wrapper id={CAMPAIGN_WRAPPER}>
                        <SquareLayout
                            campaigns={campaigns}
                            columnCount={columnCount}
                            isPlusEnabled={isPlusEnabled}
                        />
                    </Wrapper>
                </GridPageLayout>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary componentName="MyLoungeCampaigns">
            <GridLayoutProvider value={gridLayoutContextValue}>
                <GridPageLayout>
                    <Wrapper id={CAMPAIGN_WRAPPER}>{layoutMap[columnCount]}</Wrapper>
                </GridPageLayout>
            </GridLayoutProvider>
        </ErrorBoundary>
    );
};

export default memo(Grid);
